import {
  BaseApiResponse,
  DeveloperProjectsResponse,
} from "@interfaces/app.interface";
import { DevSingleProjectResponse } from "@utils/constants";
import { fetchCookie } from "@utils/cookies";
import customFetch from "@utils/customFetch";
import { toast } from "react-toastify";
import { DevProfile } from "types";

const API_BASE_URL = process.env["REACT_APP_DEVELOPER_BASE_URL"];

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE" | "PUT";

interface RequestConfig {
  method: HttpMethod;
  path: string;
  params?: Record<string, string>;
  body?: any;
  requiresAuth?: boolean;
  headers?: any;
}

async function apiRequest<T>(
  config: RequestConfig,
  signal?: AbortSignal
): Promise<T> {
  const {
    method,
    path,
    params,
    body,
    requiresAuth = true,
    headers: customHeaders,
  } = config;

  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (requiresAuth) {
    const token = fetchCookie("1Q_SPA");
    headers["Authorization"] = `Bearer ${token}`;
  }

  const finalHeaders = { ...headers, ...customHeaders };

  try {
    const response = await customFetch(url.toString(), {
      method,
      headers: finalHeaders,
      body:
        body && finalHeaders["Content-Type"] === "application/json"
          ? JSON.stringify(body)
          : body,
      signal,
    });

    const data = await response.json();

    if (!response.ok) {
      const serverError = new Error(
        data.error || data.message || "Request failed"
      );
      (serverError as any).response = data;
      throw serverError;
    }

    return data as T;
  } catch (error) {
    if (error instanceof Error && (error as any).response) {
      toast.error(error.message);
      throw error;
    }

    const errorMessage =
      error instanceof Error
        ? error.message
        : "An error occurred while processing your request.";

    // console.log(error, "error");
    toast.error(errorMessage);
    throw error;
  }
}
export const registerDeveloper = (
  payload: FormData
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_DEVELOPER_BASE_URL"]}/auth/register`,
      {
        method: "POST",
        body: payload,
      }
    )
      .then((res) => {
        return res.json();
      })
      .then((data: any) => {
        if (data.success) {
          resolve(data);
        } else {
          if (data.errors && Array.isArray(data.errors)) {
            reject({ errors: data.errors });
          } else if (data.message) {
            reject({ errors: [{ message: data.message, field: "general" }] });
          } else {
            reject({
              errors: [{ message: "Registration failed", field: "general" }],
            });
          }
        }
      })
      .catch((err) => {
        console.error("Registration error:", err);
        if (err.errors) {
          reject(err);
        } else {
          reject({
            errors: [{ message: "Network error occurred", field: "general" }],
          });
        }
      });
  });
};
export const getAllDeveloperProjects = (
  id: string,
  timeline?: string,
  page?: number,
  limit?: number
) =>
  apiRequest<DeveloperProjectsResponse>({
    method: "GET",
    path: `/project/developer-projects/${id}`,
    params: {
      ...(timeline ? { timeline } : {}),
      ...(page !== undefined ? { page: String(page) } : {}),
      ...(limit !== undefined ? { limit: String(limit) } : {}),
    },
  });

export const getDeveloperMetrics = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: "/users/listing-metrics",
    },
    signal
  );
export const getAllDevelopersProjects = (
  limit: number,
  page: number,
  signal: AbortSignal,
  searchTerm?: string
) =>
  apiRequest<DeveloperProjectsResponse>(
    {
      method: "GET",
      path: `/project/projects?timeline=all&page=${page}&limit=${limit}&search=${searchTerm}`,
    },
    signal
  );
export const getAllDevelopersProjectProperties = (
  id: string,
  signal: AbortSignal
) =>
  apiRequest<DeveloperProjectsResponse>(
    {
      method: "GET",
      path: `/project/developer-project/${id}/property`,
    },
    signal
  );

export const getSingleProjectDetails = (id: string, signal: AbortSignal) =>
  apiRequest<DevSingleProjectResponse>(
    {
      method: "GET",
      path: `/project/${id}`,
      requiresAuth: false,
    },
    signal
  );

export const createDeveloperProject = (payload: any) =>
  apiRequest<any>({
    method: "POST",
    path: "/project/create-project",
    body: payload,
  });

// export const uploadProjectImage = (id: string, payload: any) =>
//   apiRequest<any>({
//     method: "PUT",
//     path: `/project/upload-images/project/${id}`,
//     body: payload,
//     headers: {
//       "Content-Type": "multipart/form-data",
//     },
//   });
export const uploadProjectImage = (
  type: string,
  id: string,
  payload: any
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_DEVELOPER_BASE_URL"]}/project/upload-images/${type}/${id}`,
      {
        method: "PUT",
        body: payload,
      }
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// export const getAllDevelopersProjectProperties = (
//   id: string,
//   signal: AbortSignal
// ) =>
//   apiRequest<DeveloperProjectsResponse>(
//     {
//       method: "GET",
//       path: `/project/developer-project/${id}/property`,
//     },
//     signal
//   );

export const createDeveloperSingleProjectProperty = (payload: any) =>
  apiRequest<any>({
    method: "POST",
    path: "/project/create-property-for-project",
    body: payload,
  });
export const updateDeveloperSingleProjectProperty = (
  id: string,
  payload: any
) =>
  apiRequest<any>({
    method: "PUT",
    path: `/project/update-property-for-project/${id}`,
    body: payload,
  });
export const getDeveloperSingleProperty = (id: string, signal: AbortSignal) =>
  apiRequest<DeveloperProjectsResponse>(
    {
      method: "GET",
      path: `/properties/developer-property/${id}`,
    },
    signal
  );
export const editDeveloperSingleProjectProperty = (id: string, payload: any) =>
  apiRequest<any>({
    method: "PATCH",
    path: `/properties/developer-property/${id}`,
    body: payload,
  });

export const deleteDeveloperSingleProjectProperty = (
  id: string,
  payload: any
) =>
  apiRequest<any>({
    method: "DELETE",
    path: `/properties/developer-property/${id}`,
    body: payload,
  });
export const editDeveloperSingleProject = (id: string, payload: any) =>
  apiRequest<any>({
    method: "PATCH",
    path: `/project/projects/${id}`,
    body: payload,
  });

export const deleteDeveloperSingleProject = (id: string, payload: any) =>
  apiRequest<any>({
    method: "DELETE",
    path: `/project/projects/${id}`,
    body: payload,
  });

export const getDeveloperProfileInfo = (signal: AbortSignal) =>
  apiRequest<DevProfile>(
    {
      method: "GET",
      path: `/users/profile-info`,
      requiresAuth: true,
    },
    signal
  );
export const getDeveloperReviews = (id: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/reviews/developers/${id}/reviews`,
      requiresAuth: true,
    },
    signal
  );
export const createDeveloperReviews = (
  developerId: string,
  payload: any,
  signal: AbortSignal
) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/reviews/developers/${developerId}/reviews`,
      body: payload,
    },
    signal
  );

export const getAllAgents = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/users/user-type?role=agent`,
    },
    signal
  );
type DeveloperAgentsParams = {
  radius?: number;
  status?: string;
  address: string;
  page?: number;
  limit?: number;
};

export const getAllDeveloperAgents = (
  {
    status = "approved",
    address,
  }: // page = 1,
  // limit = 10,
  DeveloperAgentsParams,
  signal: AbortSignal
) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/users/agent-group?status=${status}&address=${encodeURIComponent(
        address
      )}`,
    },
    signal
  );

export const getSingleInterestDetails = (id: any, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/interest/${id}`,
      requiresAuth: true,
    },
    signal
  );
export const getUserInterests = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/user-interests`,
    },
    signal
  );

export const getUserPurchasedInterests = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/purchases
`,
    },
    signal
  );

export const getDeveloperSalesPipeline = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/developer/sales-pipeline
  `,
    },
    signal
  );

export const createUserInterest = (payload: any) =>
  apiRequest<any>({
    method: "POST",
    path: `/developer-property-transaction/user-interest`,
    body: payload,
  });
export const getDeveloperShownInterestProperties = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/developer-user-interests
  `,
    },
    signal
  );
export const createPurchaseRequest = (
  id: string,
  payload: any
): Promise<BaseApiResponse<{}>> => {
  return customFetch(
    `${process.env["REACT_APP_DEVELOPER_BASE_URL"]}/developer-property-transaction/create-purchase-request/${id}`,
    {
      method: "POST",
      body: payload,
    }
  )
    .then((res) => res.json())
    .then((data: BaseApiResponse<{}>) => {
      // Consider any successful response (including those with messages) as success
      if (data.success || data.message) {
        return data;
      }
      throw data; // Throw if no success or message
    });
};
export const completedInspection = (id: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/developer-property-transaction/complete-inspection/${id}`,
      requiresAuth: true,
    },
    signal
  );
export const bookedInspection = (id: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/developer-property-transaction/book-inspection/${id}`,
      requiresAuth: true,
    },
    signal
  );
export const getNotifications = (
  type: string,
  id: string,
  signal: AbortSignal
) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/notifications/${type}/${id}`,
      requiresAuth: true,
    },
    signal
  );

export const createForum = (payload: any): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_DEVELOPER_BASE_URL"]}/threads/create-thread`,
      {
        method: "POST",
        body: payload,
      }
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const getAllForum = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/threads/all-threads`,
      requiresAuth: true,
    },
    signal
  );
export const createPostinForum = (
  id: string,
  signal: AbortSignal,
  payload: any
) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/threads/thread/${id}/posts`,
      requiresAuth: true,
      body: payload,
    },
    signal
  );
export const getAllPostInForum = (id: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/threads/thread/${id}/posts`,
      requiresAuth: true,
    },
    signal
  );
export const getAgentRequest = (signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/agent/recent-requests`,
      requiresAuth: true,
    },
    signal
  );

export const updateDocStatus = (
  interestId: string,
  status: string,
  payload: any
) =>
  apiRequest<any>({
    method: "PUT",
    path: `/developer-property-transaction/update-documentation-status/${interestId}?action=${status}`,
    body: payload,
  });
export const AgentRequestAction = (
  action: string,
  id: string,
  signal: AbortSignal
) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/developer-property-transaction/agent/${action}-interest/${id}`,
      requiresAuth: true,
    },
    signal
  );

export const ConfirmPayment = (
  id: string,
  // payload: { status: boolean; amountPaid: string },
  signal: AbortSignal
) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/developer-property-transaction/developer/verify-payment/${id}`,
      requiresAuth: true,
      // body: payload,
    },
    signal
  );
export const sendComment = (id: string, payload: any, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/developer-property-transaction/developer-user-interest/send-message/${id}`,
      requiresAuth: true,
      body: payload,
    },
    signal
  );
export const getComments = (id: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/developer-user-interest/send-message/${id}`,
      requiresAuth: true,
    },
    signal
  );
export const fetchUserClientDocument = (id: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/user-interest/${id}`,
      requiresAuth: true,
    },
    signal
  );
export const fetchDevClientDocument = (id: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "GET",
      path: `/developer-property-transaction/developer-user-interest/${id}`,
      requiresAuth: true,
    },
    signal
  );

export const clientReceivedDoc = (transactionId: string, signal: AbortSignal) =>
  apiRequest<any>(
    {
      method: "POST",
      path: `/developer-property-transaction/developer-user-interest/client-recieve-document/${transactionId}`,
      requiresAuth: true,
    },
    signal
  );

export const uploadUserKYCDoc = (
  id: string,
  payload: any
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_DEVELOPER_BASE_URL"]}/developer-property-transaction/developer-user-interest/send-kyc/${id}`,
      {
        method: "POST",
        body: payload,
      }
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const sendClientsDocs = (
  interestId: string,
  payload: any
): Promise<BaseApiResponse<{}>> => {
  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_DEVELOPER_BASE_URL"]}/developer-property-transaction/developer-user-interest/send-document/${interestId}`,
      {
        method: "POST",
        body: payload,
      }
    )
      .then((res) => {
        return res.json();
      })
      .then((data: BaseApiResponse<{}>) => {
        if (data.success) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const updateProfile = (
  id: string,
  payload: any,
  signal: AbortSignal
) => {
  const isFormData = payload instanceof FormData;

  return new Promise((resolve, reject) => {
    return customFetch(
      `${process.env["REACT_APP_DEVELOPER_BASE_URL"]}/users/${id}`,
      {
        signal,
        method: "PUT",
        headers: isFormData
          ? undefined
          : { "Content-Type": "application/json" },
        body: isFormData ? payload : JSON.stringify(payload),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.errors) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((err) => reject(err));
  });
};
